import { KeyboardEvent, useEffect, useMemo, useState } from 'react'
import ModalPopup from 'components/ModalPopup/ModalPopup'
import { ButtonGroupAlignment, ButtonSize, ButtonStyles, ModalPopupSize } from 'lib/enums'
import { ComplexityLevel, imageComplexityScenarios } from './imageComplexityScenarios'
import './imageComplexityWizardModal.scss'

type Props = {
  show: boolean
  onCancel: () => void
  onConfirm: (selectedScenarioId: string, selectedComplexity: ComplexityLevel) => void
}

const COMPLEXITY_COLUMNS: ComplexityLevel[] = ['Low', 'Medium Low', 'Medium', 'Medium High', 'High']

const ImageComplexityWizardModal = ({ show, onCancel, onConfirm }: Props) => {
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null)

  const selectedScenario = useMemo(
    () => imageComplexityScenarios.find(scenario => scenario.id === selectedScenarioId),
    [selectedScenarioId]
  )

  const scenariosByComplexity = useMemo(
    () =>
      COMPLEXITY_COLUMNS.map(level => ({
        level,
        scenarios: imageComplexityScenarios.filter(scenario => scenario.complexity === level)
      })),
    []
  )

  useEffect(() => {
    if (!show) setSelectedScenarioId(null)
  }, [show])

  const handleScenarioKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }

    event.preventDefault()
    const columns = 5
    const lastIndex = imageComplexityScenarios.length - 1

    let nextIndex = index
    if (event.key === 'ArrowRight') nextIndex = Math.min(index + 1, lastIndex)
    if (event.key === 'ArrowLeft') nextIndex = Math.max(index - 1, 0)
    if (event.key === 'ArrowDown') nextIndex = Math.min(index + columns, lastIndex)
    if (event.key === 'ArrowUp') nextIndex = Math.max(index - columns, 0)

    const nextScenario = imageComplexityScenarios[nextIndex]
    if (!nextScenario) return

    setSelectedScenarioId(nextScenario.id)
    const nextButton = document.getElementById(`icw-tile-${nextScenario.id}`) as HTMLButtonElement | null
    nextButton?.focus()
  }

  const handleConfirm = () => {
    if (!selectedScenario) return
    onConfirm(selectedScenario.id, selectedScenario.complexity)
  }

  return (
    <ModalPopup
      id="xsd-image-complexity-wizard"
      headline="Image Complexity Wizard"
      description="Select the scenario that best matches your camera view."
      size={ModalPopupSize.Large}
      showModal={show}
      onCloseButtonclick={onCancel}
      button1Style={ButtonStyles.Primary}
      button1Size={ButtonSize.Large}
      button1Label="Confirm"
      button1Disabled={!selectedScenario}
      button1OnClickHandler={handleConfirm}
      button2Style={ButtonStyles.Tertiary}
      button2Size={ButtonSize.Large}
      button2Label="Cancel"
      button2OnClickHandler={onCancel}
      buttonGroupAlignment={ButtonGroupAlignment.Right}>
      <div className="icw-modal">
        <div className="icw-modal__grid" role="radiogroup" aria-label="Image complexity scenarios">
          {scenariosByComplexity.map(column => (
            <div key={column.level} className="icw-column">
              <div className="icw-column__title msds-text-title-2-medium msds-text-gray-10">{column.level}</div>
              <div className="icw-column__tiles">
                {column.scenarios.map(scenario => {
                  const isSelected = scenario.id === selectedScenarioId
                  const scenarioIndex = imageComplexityScenarios.findIndex(item => item.id === scenario.id)

                  return (
                    <button
                      key={scenario.id}
                      id={`icw-tile-${scenario.id}`}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`icw-tile ${isSelected ? 'icw-tile--selected' : ''}`}
                      onClick={() => setSelectedScenarioId(scenario.id)}
                      onFocus={() => setSelectedScenarioId(scenario.id)}
                      onKeyDown={event => handleScenarioKeyDown(event, scenarioIndex)}>
                      <img src={scenario.imageSrc} alt={scenario.alt} className="icw-tile__image" />
                      <div className="icw-tile__name msds-text-body-0-semibold msds-text-gray-10">{scenario.name}</div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <p className="icw-modal__disclaimer msds-text-body-1 msds-text-gray-8">
          The following images are representative examples to help guide image-complexity selection and should be
          considered approximate only.
        </p>
      </div>
    </ModalPopup>
  )
}

export default ImageComplexityWizardModal
